import { isAxiosError } from 'axios'
import { defineStore } from 'pinia'

import {
  type WorkerTypeUpdatePayload,
  destroyWorkerType,
  listWorkerTypes,
  partialUpdateWorkerType,
} from '@/api'
import { errorParser } from '@/helpers'
import type { UUID } from '@/types'
import type { WorkerType } from '@/types/worker'

import { useNotificationStore } from './notification'

interface State {
  workerTypes: {
    [typeId: UUID]: WorkerType
  }
}

export const useWorkerTypeStore = defineStore('workerType', {
  state: (): State => ({
    workerTypes: {},
  }),
  actions: {
    async list(page = 1) {
      if (page === 1) this.workerTypes = {}
      try {
        const resp = await listWorkerTypes({ page })
        this.workerTypes = {
          ...this.workerTypes,
          ...Object.fromEntries(resp.results.map((type) => [type.id, type])),
        }
        if (!resp || !resp.number || page !== resp.number) {
          throw new Error('Pagination failed listing worker types')
        }
        if (resp.next) this.list(page + 1)
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 403) {
          throw err
        } else {
          useNotificationStore().notify({ type: 'error', text: errorParser(err) })
        }
      }
    },
    async update(id: UUID, payload: WorkerTypeUpdatePayload): Promise<WorkerType> {
      const type = await partialUpdateWorkerType(id, payload)
      this.workerTypes[id] = type
      return type
    },
    async delete(id: UUID): Promise<void> {
      await destroyWorkerType(id)
      delete this.workerTypes[id]
    },
  },
})
